'use client';

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/app/store/hooks';
import {
  clearUserAppAccess,
  fetchFavorites,
  fetchRecents,
  selectFavoritesLastFetchedAt,
  selectRecentsLastFetchedAt,
  setUserAppAccessScope,
} from '@/app/store/slices/userAppAccessSlice';

const USER_APP_ACCESS_STALE_MS = 60_000;

export interface UserAppAccessWiringParams {
  scope: 'workspace' | 'main';
  tenantId: number | null;
  isAuthenticated: boolean;
}

/**
 * Centralizes the Favorites/Recents mount + scope + auth + tenant-switch effects
 * that the four home-view call-sites would otherwise each duplicate. See
 * `.kiro/specs/desktop-workspace-favorites/design.md` (D4).
 *
 * Effect sequence:
 *  - Mount / scope change / tenant switch / auth flip → clearUserAppAccess() +
 *    setUserAppAccessScope(scope) + (if authenticated) fetchFavorites + fetchRecents
 *  - Periodic staleness (separate effect) refetches when either list crosses
 *    the 60s stale threshold between renders
 *
 * Defensive rule: when scope='main', fetches always go against the null-tenant
 * bucket — the slice's `selectScopedTenantId` enforces this, and the hook
 * further ignores tenantId changes when scope==='main' by normalizing the
 * effect-dep to `null`.
 */
export function useUserAppAccessWiring(params: UserAppAccessWiringParams): void {
  const { scope, tenantId, isAuthenticated } = params;
  const dispatch = useAppDispatch();
  const favoritesLastFetchedAt = useSelector(selectFavoritesLastFetchedAt);
  const recentsLastFetchedAt = useSelector(selectRecentsLastFetchedAt);

  const effectiveTenantId = scope === 'main' ? null : tenantId;

  useEffect(() => {
    dispatch(clearUserAppAccess());
    dispatch(setUserAppAccessScope(scope));
    if (isAuthenticated) {
      dispatch(fetchFavorites());
      dispatch(fetchRecents(undefined));
    }
  }, [dispatch, scope, effectiveTenantId, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const now = Date.now();
    if (
      favoritesLastFetchedAt === null ||
      now - favoritesLastFetchedAt > USER_APP_ACCESS_STALE_MS
    ) {
      dispatch(fetchFavorites());
    }
    if (
      recentsLastFetchedAt === null ||
      now - recentsLastFetchedAt > USER_APP_ACCESS_STALE_MS
    ) {
      dispatch(fetchRecents(undefined));
    }
  }, [dispatch, isAuthenticated, favoritesLastFetchedAt, recentsLastFetchedAt]);
}
